import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FullLayout from 'layout/FullLayout';
import { interactiveExhibitions } from './data';

function normalizeImageSrc(path = '') {
  if (!path) return path;
  if (/^(?:https?:)?\/\//i.test(path) || path.startsWith('data:') || path.startsWith('/')) {
    return path;
  }
  return `/${path}`;
}

function getYouTubeEmbedUrl(videoId) {
  return `https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0&modestbranding=1`;
}

// ── Lightbox ──────────────────────────────────────────────────────────────────
function Lightbox({ src, onClose }) {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}>
      <button
        className="absolute top-5 right-5 text-white/60 hover:text-white text-3xl leading-none"
        onClick={onClose}>
        ×
      </button>
      <img
        src={normalizeImageSrc(src)}
        alt="Lightbox"
        className="max-h-[90vh] max-w-full rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

// ── LazyImage ─────────────────────────────────────────────────────────────────
function LazyImg({ src, alt, className, wrapperClass, onClick }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className={`relative overflow-hidden bg-gray-100 ${wrapperClass || ''}`}>
      <img
        src={normalizeImageSrc(src)}
        alt={alt}
        className={`transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'} ${
          className || ''
        }`}
        onLoad={() => setLoaded(true)}
        loading="lazy"
        onClick={onClick}
      />
      {!loaded && (
        <div className="absolute inset-0 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 animate-pulse" />
      )}
    </div>
  );
}

// ── Detail page ───────────────────────────────────────────────────────────────
const KinteractiveDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const exhibition = interactiveExhibitions.find((e) => e.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!exhibition) {
    return (
      <FullLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-400 mb-4">Exhibition not found.</p>
            <button
              onClick={() => navigate('/kinteractive')}
              className="text-secondary-main font-bold text-sm hover:underline">
              ← Back to KiNTERACTIVE
            </button>
          </div>
        </div>
      </FullLayout>
    );
  }

  const otherExhibitions = interactiveExhibitions.filter((e) => e.id !== id);

  return (
    <FullLayout>
      {/* ── Back button ──────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pt-8 pb-2">
        <button
          onClick={() => navigate('/kinteractive')}
          className="inline-flex items-center gap-2 text-sm font-bold text-kin-textdark hover:text-gray-900 transition-colors duration-150">
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 16l-4-4m0 0l4-4m-4 4h18" />
          </svg>
          KiNTERACTIVE
        </button>
      </div>

      {/* ── Art backdrop strip ─────────────────────────────────────────── */}
      <div className="relative h-[260px] sm:h-[340px] lg:h-[400px] overflow-hidden">
        <img
          src={normalizeImageSrc(exhibition.images[1] || exhibition.images[0])}
          alt={exhibition.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Warm cream gradient from left */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(108deg, #fcf2e9 0%, rgba(252,242,233,0.9) 35%, rgba(252,242,233,0.35) 65%, rgba(252,242,233,0) 100%)',
          }}
        />
        {/* Accent bar — bottom */}
        <div
          className="absolute bottom-0 left-0 right-0 h-[3px]"
          style={{ backgroundColor: exhibition.accentColor }}
        />
        {/* Content */}
        <div className="absolute inset-0 flex items-center">
          <div className="px-6 sm:px-10 lg:px-16 max-w-2xl">
            <p
              className="text-[10px] font-black uppercase tracking-[0.4em] mb-3"
              style={{ color: exhibition.accentColor }}>
              {exhibition.tag}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-[3rem] font-black text-kin-textdark leading-tight">
              {exhibition.name}
            </h2>
            {exhibition.subtitle && (
              <p className="text-kin-text text-sm mt-2 font-medium max-w-sm">
                {exhibition.subtitle}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-14 lg:py-20">
        {/* ── Description + Gallery layout ─────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,1.4fr] gap-12 lg:gap-16 items-start">
          {/* LEFT: About this exhibition */}
          <div>
            {/* Exhibition number badge */}
            <div className="flex items-center gap-3 mb-5">
              <span
                className="text-5xl font-black leading-none"
                style={{ color: exhibition.accentColor, opacity: 0.15 }}>
                {exhibition.order}
              </span>
              <div>
                <p
                  className="text-[10px] font-black uppercase tracking-widest"
                  style={{ color: exhibition.accentColor }}>
                  {exhibition.role}
                </p>
                <h2 className="text-2xl lg:text-3xl font-black text-gray-900 leading-tight">
                  {exhibition.name}
                </h2>
              </div>
            </div>

            {/* Divider */}
            <div
              className="h-[2px] w-10 rounded-full mb-5"
              style={{ backgroundColor: exhibition.accentColor }}
            />

            {/* Description */}
            <div className="space-y-3">
              {exhibition.description.split('\n\n').map((para, i) => (
                <p key={i} className="text-sm text-gray-500 leading-relaxed">
                  {para.trim()}
                </p>
              ))}
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mt-6">
              {exhibition.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full text-white"
                  style={{ backgroundColor: exhibition.accentColor }}>
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* RIGHT: Gallery */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div
                className="h-5 w-[3px] rounded-full"
                style={{ backgroundColor: exhibition.accentColor }}
              />
              <span className="text-[10px] font-black uppercase tracking-[0.35em] text-gray-400">
                Exhibition Gallery
              </span>
            </div>

            {/* Main image */}
            <LazyImg
              src={exhibition.images[0]}
              alt={exhibition.name}
              wrapperClass="aspect-video rounded-2xl mb-3 cursor-pointer ring-1 ring-gray-200 hover:ring-2 transition-all duration-200"
              className="w-full h-full object-cover"
              onClick={() => setLightboxSrc(exhibition.images[0])}
            />

            {/* Thumbnails grid */}
            <div className="grid grid-cols-2 gap-3">
              {exhibition.images.slice(1).map((img, i) => (
                <LazyImg
                  key={i}
                  src={img}
                  alt={`${exhibition.name} ${i + 2}`}
                  wrapperClass="aspect-square rounded-xl cursor-pointer ring-1 ring-gray-200 hover:ring-2 transition-all duration-200"
                  className="w-full h-full object-cover"
                  onClick={() => setLightboxSrc(img)}
                />
              ))}
            </div>
            <p className="text-[10px] text-gray-300 mt-2">Tap an image to expand</p>
          </div>
        </div>

        {/* ── Video — full width ────────────────────────────────────────── */}
        {exhibition.videoId && (
          <div className="mt-14 lg:mt-20">
            <div className="flex items-center gap-3 mb-5">
              <div
                className="h-5 w-[3px] rounded-full"
                style={{ backgroundColor: exhibition.accentColor }}
              />
              <span className="text-[10px] font-black uppercase tracking-[0.35em] text-gray-400">
                Exhibition Video
              </span>
            </div>
            <div
              className="relative w-full overflow-hidden rounded-2xl bg-black shadow-xl"
              style={{ paddingBottom: '50%' }}>
              <iframe
                src={getYouTubeEmbedUrl(exhibition.videoId)}
                title={`${exhibition.name} – Video`}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </div>
            <a
              href={`https://www.youtube.com/watch?v=${exhibition.videoId}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 mt-3 text-xs font-bold text-gray-400 hover:text-gray-600 transition-colors duration-150">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
              Watch on YouTube
            </a>
          </div>
        )}
      </div>

      {/* ── Other Exhibitions ─────────────────────────────────────────────── */}
      <section className="px-6 sm:px-10 lg:px-16 py-14">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-5 w-[3px] rounded-full bg-secondary-main" />
            <span className="text-[10px] font-black uppercase tracking-[0.35em] text-gray-400">
              Other Exhibitions
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {otherExhibitions.map((e) => (
              <div
                key={e.id}
                onClick={() => navigate(`/kinteractive/${e.id}`)}
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 flex gap-0">
                {/* Image */}
                <div className="relative w-36 sm:w-44 flex-shrink-0 overflow-hidden">
                  <img
                    src={normalizeImageSrc(e.mainImage)}
                    alt={e.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div
                    className="absolute bottom-0 left-0 right-0 h-1"
                    style={{ backgroundColor: e.accentColor }}
                  />
                </div>

                {/* Info */}
                <div className="p-4 flex flex-col justify-between flex-1 min-w-0">
                  <div>
                    <p
                      className="text-[9px] font-black uppercase tracking-widest mb-1"
                      style={{ color: e.accentColor }}>
                      {e.tag}
                    </p>
                    <h3 className="font-black text-gray-900 text-base leading-tight">{e.name}</h3>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">{e.subtitle}</p>
                  </div>
                  <div
                    className="inline-flex items-center gap-1 text-xs font-bold mt-3"
                    style={{ color: e.accentColor }}>
                    View Exhibition
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <button
              onClick={() => navigate('/kinteractive')}
              className="inline-flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-gray-800 transition-colors duration-150">
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 16l-4-4m0 0l4-4m-4 4h18" />
              </svg>
              Back to all exhibitions
            </button>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </FullLayout>
  );
};

export default KinteractiveDetail;
